import { getFieldTitle } from '@src/utils/fieldDefinitionUtils';
import { fixedRateLoanFieldDefinitions } from '@src/utils/OTC/fixedRateLoanFieldDefinitions';
import { floatingRateLoanFieldDefinitions } from '@src/utils/OTC/floatingRateLoanFieldDefinitions';
import {
  formatLoanSecurityTypeDisplay,
  isUnsecuredLoanSecurityType,
  resolveLoanCollateralAssetDisplay,
} from '@src/utils/OTC/loanSecurity';
import { formatLoanPaymentType } from '@src/utils/OTC/otcUtils';
import { AssetRenderer, type OTCFixedRateLoanProductDetails } from '@talos/kyoko';
import type { ReactNode } from 'react';
import { InspectorText } from '../InspectorText';

type LoanProductDetailsSource = Pick<
  OTCFixedRateLoanProductDetails,
  'BorrowedAsset' | 'CollateralAsset' | 'LoanSecurityType' | 'LoanPaymentType'
>;

type LoanFieldDefinitions = Pick<
  typeof fixedRateLoanFieldDefinitions,
  'borrowedAsset' | 'collateralization' | 'collateralAsset' | 'loanPaymentType'
>;

const defaultLoanFieldDefinitions: LoanFieldDefinitions = {
  borrowedAsset: fixedRateLoanFieldDefinitions.borrowedAsset,
  collateralization: fixedRateLoanFieldDefinitions.collateralization,
  collateralAsset: fixedRateLoanFieldDefinitions.collateralAsset,
  loanPaymentType: fixedRateLoanFieldDefinitions.loanPaymentType,
};

export function buildLoanAssetDealInformationFields(
  productDetails: LoanProductDetailsSource,
  fieldDefinitions: LoanFieldDefinitions = defaultLoanFieldDefinitions
): Array<{ fieldName: string; fieldValue: ReactNode }> {
  const isUnsecured = isUnsecuredLoanSecurityType(productDetails.LoanSecurityType);

  const fields: Array<{ fieldName: string; fieldValue: ReactNode }> = [
    {
      fieldName: getFieldTitle(fieldDefinitions.borrowedAsset),
      fieldValue: <AssetRenderer asset={productDetails.BorrowedAsset} colorful />,
    },
    {
      fieldName: getFieldTitle(fieldDefinitions.collateralization),
      fieldValue: (
        <InspectorText color={isUnsecured ? 'colorTextDefault' : undefined}>
          {formatLoanSecurityTypeDisplay(productDetails.LoanSecurityType)}
        </InspectorText>
      ),
    },
  ];

  if (!isUnsecured) {
    const collateralDisplay = resolveLoanCollateralAssetDisplay(
      productDetails.LoanSecurityType,
      productDetails.CollateralAsset
    );
    fields.push({
      fieldName: getFieldTitle(fieldDefinitions.collateralAsset),
      fieldValue: collateralDisplay ? (
        <AssetRenderer asset={collateralDisplay} colorful />
      ) : (
        <InspectorText>-</InspectorText>
      ),
    });
  }

  return fields;
}

export function buildLoanPaymentTypeDealInformationField(
  productDetails: Pick<LoanProductDetailsSource, 'LoanPaymentType'>,
  fieldDefinitions: Pick<LoanFieldDefinitions, 'loanPaymentType'> = defaultLoanFieldDefinitions
): { fieldName: string; fieldValue: ReactNode } {
  return {
    fieldName: getFieldTitle(fieldDefinitions.loanPaymentType),
    fieldValue: <InspectorText>{formatLoanPaymentType(productDetails.LoanPaymentType)}</InspectorText>,
  };
}

export const floatingLoanFieldDefinitionsForInspector: LoanFieldDefinitions = {
  borrowedAsset: floatingRateLoanFieldDefinitions.borrowedAsset,
  collateralization: floatingRateLoanFieldDefinitions.collateralization,
  collateralAsset: floatingRateLoanFieldDefinitions.collateralAsset,
  loanPaymentType: floatingRateLoanFieldDefinitions.loanPaymentType,
};
